"use client";

import { useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Heart } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";

import { trackRecipe } from "@/lib/analytics";
import { springBouncy } from "@/lib/animations";
import { cn } from "@/lib/utils";

interface FavoriteButtonProps {
  recipeId: string;
  initial: boolean;
  className?: string;
  onAuthGate?: () => boolean;
}

export function FavoriteButton({ recipeId, initial, className, onAuthGate }: FavoriteButtonProps) {
  const t = useTranslations("recipeDetail");
  const queryClient = useQueryClient();
  const [favorited, setFavorited] = useState(initial);
  const [bounceKey, setBounceKey] = useState(0);
  const inflightRef = useRef<AbortController | null>(null);
  const reducedMotion = useReducedMotion();

  const toggle = () => {
    if (onAuthGate && !onAuthGate()) return;
    const next = !favorited;
    setFavorited(next);
    setBounceKey((k) => k + 1);
    trackRecipe(next ? "favorited" : "unfavorited", { recipeId });

    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;

    fetch(`/api/recipes/${recipeId}/favorite`, {
      method: next ? "POST" : "DELETE",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        queryClient.invalidateQueries({ queryKey: ["recipes", "search"] });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setFavorited(!next);
      });
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={favorited ? t("unfavorite") : t("favorite")}
      aria-pressed={favorited}
      className={cn(
        "inline-flex size-9 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70",
        className
      )}
    >
      <motion.span
        key={bounceKey}
        initial={bounceKey === 0 ? false : { scale: 1.4 }}
        animate={{ scale: 1 }}
        transition={reducedMotion ? { duration: 0 } : springBouncy}
        className="inline-flex items-center justify-center"
      >
        <Heart
          className={cn(
            "size-4 transition-colors duration-200",
            favorited ? "fill-rose-400 text-rose-400" : "fill-transparent text-foreground"
          )}
          aria-hidden
        />
      </motion.span>
    </button>
  );
}
